import React, { useEffect, useState } from "react";
import {
  Alert,
  Box,
  Button,
  Chip,
  Container,
  FormControl,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
  Checkbox,
} from "@mui/material";
import AdminNavbar from "./AdminNavbar";
import api from "lib/api/api";

const statusColors = {
  PENDING: "warning",
  CREATED: "warning",
  SUCCEEDED: "success",
  FAILED: "error",
  CANCELLED: "default",
  EXPIRED: "default",
};

const AdminOnlinePaymentsPage = () => {
  const [provider, setProvider] = useState("");
  const [status, setStatus] = useState("");
  const [payments, setPayments] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [selectedTransactionIds, setSelectedTransactionIds] = useState([]);

  const loadPayments = async () => {
    setLoading(true);
    setError("");
    try {
      const params = {};
      if (provider) params.provider = provider;
      if (status) params.status = status;
      const response = await api.get("/admin/payments/online", { params });
      setPayments(Array.isArray(response.data) ? response.data : []);
    } catch (err) {
      setError(err.response?.data?.message || "Could not load online payments");
    } finally {
      setLoading(false);
    }
  };

  const toggleTransactionSelection = (transactionId) => {
    setSelectedTransactionIds((prev) =>
      prev.includes(transactionId)
        ? prev.filter((id) => id !== transactionId)
        : [...prev, transactionId]
    );
  };

  const toggleAllTransactions = () => {
    const ids = payments.map((payment) => payment.transactionId);
    const allSelected = ids.length > 0 && ids.every((id) => selectedTransactionIds.includes(id));
    setSelectedTransactionIds((prev) =>
      allSelected
        ? prev.filter((id) => !ids.includes(id))
        : Array.from(new Set([...prev, ...ids]))
    );
  };

  useEffect(() => {
    loadPayments();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [provider, status]);

  const archivePayment = async (transactionId) => {
    if (!window.confirm("Archive/cancel this transaction?")) return;
    setError("");
    try {
      await api.post(`/admin/payments/online/${transactionId}/archive`);
      await loadPayments();
    } catch (err) {
      setError(err.response?.data?.message || "Could not archive transaction");
    }
  };

  const deletePayment = async (transactionId) => {
    if (!window.confirm("Delete this transaction for cleanup? This cannot be undone.")) return;
    setError("");
    try {
      await api.delete(`/admin/payments/online/${transactionId}`);
      setSelectedTransactionIds((prev) => prev.filter((id) => id !== transactionId));
      await loadPayments();
    } catch (err) {
      setError(err.response?.data?.message || "Could not delete transaction");
    }
  };

  const deleteSelectedPayments = async () => {
    if (!selectedTransactionIds.length) return;
    if (!window.confirm(`Delete ${selectedTransactionIds.length} selected transaction(s)? Succeeded payments will be blocked.`)) return;
    setError("");
    try {
      await api.post("/admin/payments/online/delete-bulk", selectedTransactionIds);
      setSelectedTransactionIds([]);
      await loadPayments();
    } catch (err) {
      setError(err.response?.data?.message || "Could not delete selected transactions");
      await loadPayments();
    }
  };

  return (
    <div className="admin-dashboard-layout">
      <AdminNavbar />
      <Container className="admin-dashboard-content" maxWidth={false}>
        <Box sx={{ my: 4 }}>
          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: { xs: "stretch", md: "center" },
              flexDirection: { xs: "column", md: "row" },
              gap: 2,
              mb: 2,
            }}
          >
            <Box>
              <Typography variant="h4" fontWeight={800}>
                Online Payment History
              </Typography>
              <Typography color="text.secondary">
                Stripe and PayPal transactions, webhook status, and cleanup actions.
              </Typography>
            </Box>
            <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap" }}>
              <FormControl size="small" sx={{ minWidth: 160 }}>
                <InputLabel>Provider</InputLabel>
                <Select label="Provider" value={provider} onChange={(event) => setProvider(event.target.value)}>
                  <MenuItem value="">All</MenuItem>
                  <MenuItem value="STRIPE">Stripe</MenuItem>
                  <MenuItem value="PAYPAL">PayPal</MenuItem>
                </Select>
              </FormControl>
              <FormControl size="small" sx={{ minWidth: 160 }}>
                <InputLabel>Status</InputLabel>
                <Select label="Status" value={status} onChange={(event) => setStatus(event.target.value)}>
                  <MenuItem value="">All</MenuItem>
                  <MenuItem value="CREATED">Created</MenuItem>
                  <MenuItem value="PENDING">Pending</MenuItem>
                  <MenuItem value="SUCCEEDED">Succeeded</MenuItem>
                  <MenuItem value="FAILED">Failed</MenuItem>
                  <MenuItem value="CANCELLED">Cancelled</MenuItem>
                  <MenuItem value="EXPIRED">Expired</MenuItem>
                </Select>
              </FormControl>
              <Button variant="outlined" onClick={loadPayments} disabled={loading}>
                Refresh
              </Button>
              <Button
                variant="outlined"
                color="error"
                onClick={deleteSelectedPayments}
                disabled={loading || !selectedTransactionIds.length}
              >
                Delete selected ({selectedTransactionIds.length})
              </Button>
            </Box>
          </Box>

          {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

          <TableContainer component={Paper}>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell padding="checkbox">
                    <Checkbox
                      size="small"
                      checked={payments.length > 0 && payments.every((payment) => selectedTransactionIds.includes(payment.transactionId))}
                      indeterminate={payments.some((payment) => selectedTransactionIds.includes(payment.transactionId)) && !payments.every((payment) => selectedTransactionIds.includes(payment.transactionId))}
                      onChange={toggleAllTransactions}
                    />
                  </TableCell>
                  <TableCell>Created</TableCell>
                  <TableCell>Provider</TableCell>
                  <TableCell>Student</TableCell>
                  <TableCell>Email</TableCell>
                  <TableCell>Course</TableCell>
                  <TableCell>Amount</TableCell>
                  <TableCell>Status</TableCell>
                  <TableCell>Request</TableCell>
                  <TableCell>Provider Session</TableCell>
                  <TableCell>Failure</TableCell>
                  <TableCell align="right">Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {payments.map((payment) => {
                  const isSucceeded = payment.status === "SUCCEEDED";
                  return (
                    <TableRow key={payment.transactionId} selected={selectedTransactionIds.includes(payment.transactionId)}>
                      <TableCell padding="checkbox">
                        <Checkbox
                          size="small"
                          checked={selectedTransactionIds.includes(payment.transactionId)}
                          onChange={() => toggleTransactionSelection(payment.transactionId)}
                        />
                      </TableCell>
                      <TableCell>{payment.createdAt ? new Date(payment.createdAt).toLocaleString() : "-"}</TableCell>
                      <TableCell>{payment.provider}</TableCell>
                      <TableCell>{payment.studentName || "-"}</TableCell>
                      <TableCell>{payment.studentEmail || "-"}</TableCell>
                      <TableCell>{payment.courseTitle || "-"}</TableCell>
                      <TableCell>{payment.currency} {Number(payment.amount || 0).toFixed(2)}</TableCell>
                      <TableCell>
                        <Chip size="small" color={statusColors[payment.status] || "default"} label={payment.status} />
                      </TableCell>
                      <TableCell>
                        #{payment.studentCourseRequestId || "-"} {payment.requestStatus ? `· ${payment.requestStatus}` : ""}
                      </TableCell>
                      <TableCell sx={{ maxWidth: 220, wordBreak: "break-word" }}>
                        {payment.providerSessionId || "-"}
                      </TableCell>
                      <TableCell sx={{ maxWidth: 260, wordBreak: "break-word" }}>
                        {payment.failureReason || "-"}
                      </TableCell>
                      <TableCell align="right">
                        <Box sx={{ display: "flex", justifyContent: "flex-end", gap: 1 }}>
                          {payment.checkoutUrl && !isSucceeded && (
                            <Button
                              size="small"
                              href={payment.checkoutUrl}
                              target="_blank"
                              rel="noopener noreferrer"
                            >
                              Checkout
                            </Button>
                          )}
                          <Button
                            size="small"
                            color="warning"
                            disabled={isSucceeded || loading}
                            onClick={() => archivePayment(payment.transactionId)}
                          >
                            Archive
                          </Button>
                          <Button
                            size="small"
                            color="error"
                            disabled={isSucceeded || loading}
                            onClick={() => deletePayment(payment.transactionId)}
                          >
                            Delete
                          </Button>
                        </Box>
                      </TableCell>
                    </TableRow>
                  );
                })}
                {!payments.length && (
                  <TableRow>
                    <TableCell colSpan={12} align="center">
                      {loading ? "Loading..." : "No online payments found."}
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </Box>
      </Container>
    </div>
  );
};

export default AdminOnlinePaymentsPage;
